import { trackEvent } from "@/lib/analytics";
import { useState } from "react";
import { z } from "zod";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOBreadcrumb from "@/components/SEOBreadcrumb";
import { useLanguage } from "@/i18n/LanguageContext";
import { useLocalizedRoute } from "@/hooks/useLocalizedRoute";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Send, HeartHandshake, MessageCircleQuestion } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useSEO } from "@/hooks/useSEO";
import { sendContactFormEmails } from "@/lib/appEmails.functions";

const INQUIRY_TYPES = ["support", "pricing", "suggestion", "general"] as const;

const INQUIRY_TYPE_LABELS: Record<(typeof INQUIRY_TYPES)[number], string> = {
  support: "typeOptionSupport",
  pricing: "typeOptionPricing",
  suggestion: "typeOptionSuggestion",
  general: "typeOptionGeneral",
};



const contactSchema = z.object({
  name: z.string().trim().min(1).max(100),
  email: z.string().trim().email().max(255),
  type: z.enum(INQUIRY_TYPES),
  message: z.string().trim().min(1).max(2000),
});

const Contact = () => {
  const { lang, t } = useLanguage();
  useLocalizedRoute();

  useSEO({
    title: { es: t("seo", "contactTitle"), en: t("seo", "contactTitle") },
    description: { es: t("seo", "contactDesc"), en: t("seo", "contactDesc") },
    keywords: { es: t("seo", "contactKeywords"), en: t("seo", "contactKeywords") },
    imageAlt: { es: t("seo", "contactImageAlt"), en: t("seo", "contactImageAlt") },
    path: lang === "es" ? "/contacto" : "/en/contact",
    pathEs: "/contacto",
    pathEn: "/en/contact",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: lang === "es" ? "Contacto" : "Contact",
      url: lang === "es" ? "https://retaillemon.com/contacto" : "https://retaillemon.com/en/contact",
      mainEntity: {
        "@type": "Organization",
        name: "Retail Lemon",
      },
    },
  });

  const [form, setForm] = useState({ name: "", email: "", type: "", message: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [privacyOk, setPrivacyOk] = useState(false);

  const l = (key: string) => t("contactPage", key);
  const typeLabel = (type: (typeof INQUIRY_TYPES)[number]) => t("contactPage", INQUIRY_TYPE_LABELS[type]);

  const validate = () => {
    const result = contactSchema.safeParse(form);
    if (result.success) {
      setErrors({});
      return true;
    }
    const fieldErrors: Record<string, string> = {};
    result.error.issues.forEach((issue) => {
      const field = issue.path[0] as string;
      if (field === "email" && issue.code === "invalid_string") {
        fieldErrors[field] = l("invalidEmail");
      } else {
        fieldErrors[field] = l("required");
      }
    });
    setErrors(fieldErrors);
    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSending(true);
    const { error } = await supabase.from("contact_messages").insert({
      name: form.name.trim(),
      email: form.email.trim(),
      type: form.type as any,
      message: form.message.trim(),
    } as any);

    setSending(false);

    if (error) {
      toast({ title: l("errorTitle"), description: l("errorDesc"), variant: "destructive" });
    } else {
      trackEvent("contact", { method: "formulario", contact_type: form.type });
      // Fire-and-forget: confirmation to user + admin notification
      sendContactFormEmails({
        data: {
          name: form.name.trim(),
          email: form.email.trim(),
          type: typeLabel(form.type as (typeof INQUIRY_TYPES)[number]),
          message: form.message.trim(),
          createdAt: new Date().toLocaleString("es-ES", { timeZone: "Europe/Madrid" }),
          lang: lang === "en" ? "en" : "es",
        },
      }).catch(() => {});
      setSent(true);
      setForm({ name: "", email: "", type: "", message: "" });
      toast({ title: l("successTitle"), description: l("successDesc") });
    }
  };

  return (
    <>
      <Header />
      <SEOBreadcrumb
        items={[
          { label: "Retail Lemon", href: "/" },
          { label: lang === "es" ? "Contacto" : "Contact" },
        ]}
      />
      <main className="min-h-screen bg-background pt-8 pb-20">
        <section className="container max-w-5xl">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-16 items-start">
            {/* Left column — Form */}
            <div className="order-1">
              <h1 className="font-display text-2xl md:text-4xl font-bold text-foreground mb-3">
                {l("title")}
              </h1>
              <p className="text-muted-foreground text-sm md:text-base mb-8">
                {l("subtitle")}
              </p>

              {sent ? (
                <div className="rounded-2xl border border-primary/20 bg-primary/5 p-10 text-center">
                  <p className="text-2xl mb-2">🍋</p>
                  <h2 className="font-display text-xl font-bold mb-2">{l("sentTitle")}</h2>
                  <p className="text-muted-foreground">{l("sentDesc")}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="name">{l("nameLabel")}</Label>
                    <Input
                      id="name"
                      placeholder={l("namePlaceholder")}
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      maxLength={100}
                    />
                    {errors['name'] && <p className="text-sm text-destructive">{errors['name']}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">{l("emailLabel")}</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={l("emailPlaceholder")}
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      maxLength={255}
                    />
                    {errors['email'] && <p className="text-sm text-destructive">{errors['email']}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="type">{l("typeLabel")}</Label>
                    <Select
                      value={form.type}
                      onValueChange={(value) => setForm({ ...form, type: value })}
                    >
                      <SelectTrigger id="type" className="w-full">
                        <SelectValue placeholder={l("typePlaceholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        {INQUIRY_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {typeLabel(type)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors['type'] && <p className="text-sm text-destructive">{errors['type']}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">{l("messageLabel")}</Label>
                    <Textarea
                      id="message"
                      placeholder={l("messagePlaceholder")}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      maxLength={2000}
                      rows={5}
                    />
                    {errors['message'] && <p className="text-sm text-destructive">{errors['message']}</p>}
                  </div>

                  <label htmlFor="contact-privacy" className="flex items-start gap-2 cursor-pointer select-none text-xs text-foreground/80 leading-relaxed">
                    <Checkbox id="contact-privacy" checked={privacyOk} onCheckedChange={(v) => setPrivacyOk(v === true)} className="mt-0.5 shrink-0" />
                    <span>
                      {lang === "es" ? "He leído y acepto la " : "I have read and accept the "}
                      <Link to={lang === "es" ? "/privacidad" : "/en/privacy-policy"} target="_blank" className="font-semibold underline underline-offset-2">
                        {lang === "es" ? "Política de Privacidad" : "Privacy Policy"}
                      </Link>.
                    </span>
                  </label>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={sending || !privacyOk}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {sending ? l("submitting") : l("submit")}
                  </Button>
                </form>

              )}
            </div>

            {/* Right column — Support & trust */}
            <div className="order-2 lg:pt-12 space-y-6">
              <div className="rounded-2xl border border-border/60 bg-card p-6 md:p-8">
                <div className="mb-4 inline-flex items-center justify-center rounded-full bg-primary/10 p-3">
                  <HeartHandshake className="h-5 w-5 text-primary-foreground" />
                </div>
                <h2 className="font-display text-lg font-semibold text-foreground mb-2">
                  {l("closeSupportTitle")}
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {l("closeSupportDesc")}
                </p>

              </div>

              <div className="rounded-2xl border border-border/60 bg-card p-6 md:p-8">
                <div className="mb-4 inline-flex items-center justify-center rounded-full bg-primary/10 p-3">
                  <MessageCircleQuestion className="h-5 w-5 text-primary-foreground" />
                </div>
                <h2 className="font-display text-lg font-semibold text-foreground mb-2">
                  {l("immediateHelpTitle")}
                </h2>
                <p className="text-sm text-muted-foreground mb-4">
                  {t("faq", "title")}
                </p>
                <Link
                  to={lang === "es" ? "/faq" : "/en/faq"}
                  onClick={() => window.scrollTo(0, 0)}
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 w-full"
                >
                  {l("immediateHelpCta")}
                </Link>


              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default Contact;
